import { useState } from 'react';

import { RunSchema } from '@/types/schema';

function bootstrapSecrets(secretKeys: string[]): Record<string, string> {
  const secrets: Record<string, string> = {};
  for (const key of secretKeys) {
    const value = window.localStorage.getItem(getStorageSecretKey(key));
    secrets[key] = value || '';
  }
  return secrets;
}

export function gatherSecrets(serialized: object | undefined | null) {
  if (serialized == null) return [];

  const ids: string[] = [];
  // DFS to find all secrets, reusing the reviver from the JSON.parse
  JSON.stringify(serialized, (_, value) => {
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      'lc' in value &&
      'type' in value &&
      'id' in value &&
      value.lc === 1 &&
      value.type === 'secret'
    ) {
      const [key] = value.id;
      ids.push(key);
    }

    return value;
  });

  return [...new Set(ids)];
}

export function getStorageSecretKey(value: string) {
  return `ls:${value.toUpperCase()}`;
}

const secretsIdentity = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

export function usePlaygroundSecrets(props: {
  serialized: RunSchema['serialized'] | Array<RunSchema['serialized']> | null;
}) {
  const gatheredSecrets = gatherSecrets(props.serialized);
  const [secrets, setSecrets] = useState<Record<string, string>>(() =>
    bootstrapSecrets(gatheredSecrets)
  );
  // need open ai key for prompt canvas
  const [dedicatedOpenAISecret, setDedicatedOpenAISecret] = useState<string>(
    bootstrapSecrets(['OPENAI_API_KEY'])['OPENAI_API_KEY']
  );

  const [prevGatheredSecrets, setPrevGatheredSecrets] =
    useState(gatheredSecrets);

  if (!secretsIdentity(gatheredSecrets, prevGatheredSecrets)) {
    setPrevGatheredSecrets(gatheredSecrets);
    setSecrets(bootstrapSecrets(gatheredSecrets));
  }

  const update = (secrets: Record<string, string>) => {
    for (const key in secrets) {
      window.localStorage.setItem(getStorageSecretKey(key), secrets[key]);
      if (key === 'OPENAI_API_KEY') {
        setDedicatedOpenAISecret(secrets[key]);
      }
    }

    // if the manifests have OPENAI_API_KEY, we need to include it
    // otherwise don't include it
    if (gatheredSecrets.includes('OPENAI_API_KEY')) {
      setSecrets((prev) => ({ ...prev, ...secrets }));
    } else {
      setSecrets((prev) => {
        // Filter out OPENAI_API_KEY from previous secrets
        const filteredPrev = Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== 'OPENAI_API_KEY')
        );

        // Filter out OPENAI_API_KEY from new secrets
        const filteredNew = Object.fromEntries(
          Object.entries(secrets).filter(([key]) => key !== 'OPENAI_API_KEY')
        );

        // Merge the filtered previous secrets with the filtered new secrets
        return { ...filteredPrev, ...filteredNew };
      });
    }
  };

  return [secrets, update, dedicatedOpenAISecret] as const;
}
